import React, { useEffect, useRef, useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { Card } from './card';
import { Badge } from './badge';
import { Input } from './input';
import { Button } from './button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';
import { cn } from '../../lib/utils';
import { formatDisplayDate } from '../../lib/dateUtils';

interface DataField {
  label: string;
  value: string | number;
  format?: 'currency' | 'date' | 'text';
  // Optional currency code to format monetary values (e.g., 'EUR', 'USD').
  // Used only when format === 'currency'.
  currencyCode?: string;
}

interface ItemCardProps {
  // Common props
  title: string; // fileName for invoice, transactionId for transaction
  isMatched: boolean;
  data: DataField[];
  isSelected: boolean;
  onSelect: (selected: boolean, percentage?: number) => void;

  // Type-specific props
  type: 'invoice' | 'transaction';
  onViewItem?: () => void; // onViewInvoice for invoice, onViewTransaction for transaction

  // Invoice-specific props for partial matching
  allowPartialMatch?: boolean;
  defaultPercentage?: number;

  // UI control props
  hideSelection?: boolean;
  className?: string;

  // Selection state props for not-matched view
  oppositeSelected?: boolean; // If true, show "Match" button instead of "Select"
  otherSelected?: boolean; // If true, grey out this card (another item on same side is selected)
  onMatch?: (percentage?: number) => void; // Called when "Match" button is clicked

  // Delete functionality props
  showDeleteButton?: boolean; // If true, show delete button
  onDelete?: () => void; // Called when delete button is clicked
  isDeleting?: boolean; // If true, show loading state on delete button
}

const PRESET_PERCENTAGE_OPTIONS = new Set(['10', '20', '50', '100']);

export const ItemCard: React.FC<ItemCardProps> = ({
  title,
  isMatched,
  data,
  isSelected,
  onSelect,
  type,
  onViewItem,
  allowPartialMatch = false,
  defaultPercentage = 100,
  hideSelection = false,
  className,
  oppositeSelected = false,
  otherSelected = false,
  onMatch,
  showDeleteButton = false,
  onDelete,
  isDeleting = false,
}) => {
  const [percentage, setPercentage] = useState<number>(defaultPercentage);
  const [customInputValue, setCustomInputValue] = useState<string>(defaultPercentage.toString());
  const [isEditingCustom, setIsEditingCustom] = useState<boolean>(
    !PRESET_PERCENTAGE_OPTIONS.has(defaultPercentage.toString())
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const customPercentageInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setPercentage(defaultPercentage);
    setCustomInputValue(defaultPercentage.toString());
    setIsEditingCustom(!PRESET_PERCENTAGE_OPTIONS.has(defaultPercentage.toString()));
  }, [defaultPercentage]);

  const selectValue = !isEditingCustom && PRESET_PERCENTAGE_OPTIONS.has(percentage.toString())
    ? percentage.toString()
    : 'custom';

  const formatValue = (
    value: string | number,
    format?: 'currency' | 'date' | 'text',
    currencyCode?: string
  ) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (format === 'currency' && typeof value === 'number') {
      return value.toFixed(2) + ' ' + currencyCode;
    }
    if (format === 'date' && typeof value === 'string') {
      return formatDisplayDate(value) || value;
    }
    return value.toString();
  };

  const handleSelect = (selected: boolean) => {
    if (selected && allowPartialMatch) {
      onSelect(selected, percentage);
    } else {
      onSelect(selected);
    }
  };

  const handleCustomPercentageChange = (value: string) => {
    setIsEditingCustom(true);

    if (value.trim() === '') {
      setCustomInputValue('');
      return;
    }

    const parsedValue = Number.parseInt(value, 10);

    if (Number.isNaN(parsedValue)) {
      return;
    }

    const clampedValue = Math.min(100, Math.max(0, parsedValue));
    setPercentage(clampedValue);
    setCustomInputValue(clampedValue.toString());

    if (isSelected) {
      onSelect(true, clampedValue);
    }
  };

  const handlePresetPercentageChange = (value: string) => {
    if (value === 'custom') {
      setIsEditingCustom(true);
      requestAnimationFrame(() => {
        customPercentageInputRef.current?.focus();
      });
      return;
    }

    const numValue = Number.parseInt(value, 10);
    if (Number.isNaN(numValue)) {
      return;
    }

    const clampedValue = Math.min(100, Math.max(0, numValue));
    setPercentage(clampedValue);
    setCustomInputValue(clampedValue.toString());
    setIsEditingCustom(false);

    if (isSelected) {
      onSelect(true, clampedValue);
    }
  };

  const ensureCustomValueOnBlur = () => {
    if (customInputValue.trim() === '') {
      setCustomInputValue(percentage.toString());
      setIsEditingCustom(!PRESET_PERCENTAGE_OPTIONS.has(percentage.toString()));
      return;
    }

    const parsedValue = Number.parseInt(customInputValue, 10);

    if (Number.isNaN(parsedValue)) {
      setCustomInputValue(percentage.toString());
      setIsEditingCustom(!PRESET_PERCENTAGE_OPTIONS.has(percentage.toString()));
      return;
    }

    const clampedValue = Math.min(100, Math.max(0, parsedValue));
    if (clampedValue !== percentage) {
      setPercentage(clampedValue);
      if (isSelected) {
        onSelect(true, clampedValue);
      }
    }
    setCustomInputValue(clampedValue.toString());
    setIsEditingCustom(!PRESET_PERCENTAGE_OPTIONS.has(clampedValue.toString()));
  };

  const getViewButtonText = () => {
    return type === 'invoice' ? 'View Invoice' : 'View Transaction';
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete();
    }
    setShowDeleteConfirm(false);
  };

  // Compute display title and reorder fields according to requirements
  const computeDisplayState = () => {
    let displayTitle = title;
    let fields = [...data];

    // For invoices, move Invoice Number to the heading and remove from fields
    if (type === 'invoice') {
      const invoiceNumberField = fields.find(f => f.label === 'Invoice Number');
      if (invoiceNumberField) {
        const formatted = 'Invoice Number: ' + formatValue(invoiceNumberField.value, invoiceNumberField.format);
        displayTitle = formatted || title;
        fields = fields.filter(f => f.label !== 'Invoice Number');
      }
    }

    // Desired order by type
    const desiredOrder =
      type === 'transaction'
        ? ['Amount', 'Date', 'From', 'To', 'Purpose']
        : ['Amount', 'Invoice Date', 'Customer', 'Vendor'];

    const orderMap = new Map<string, number>();
    desiredOrder.forEach((label, idx) => orderMap.set(label, idx));

    const sorted = fields.slice().sort((a, b) => {
      const ai = orderMap.has(a.label) ? (orderMap.get(a.label) as number) : desiredOrder.length + 100;
      const bi = orderMap.has(b.label) ? (orderMap.get(b.label) as number) : desiredOrder.length + 100;
      return ai - bi;
    });

    return { displayTitle, sortedFields: sorted };
  };

  const { displayTitle, sortedFields } = computeDisplayState();

  return (
    <Card className={cn(
      "px-8 py-6 flex flex-col h-[20rem] bg-white transition-all",
      isSelected && "border-2 border-blue-500",
      otherSelected && "opacity-40",
      className
    )}>
      {/* Header */
      }
      <div className="flex items-start justify-between gap-4 mb-4">
        <h3 className="font-bold text-sm break-words pr-2 flex-1">{displayTitle}</h3>
        <Badge
          variant="outline"
          className={
            isMatched
              ? "bg-green-100 text-green-700 border-green-700 hover:bg-green-200"
              : "bg-red-100 text-red-700 border-red-700 hover:bg-red-200"
          }
        >
          {isMatched ? "Matched" : "Not Matched"}
        </Badge>
      </div>

      {/* Data Section - flex-grow to take available space and center content */}
      <div className="flex-1 flex flex-col space-y-2 overflow-hidden">
        {sortedFields.map((field, index) => (
          <div key={index} className="flex items-start justify-between gap-3 overflow-hidden">
            <span className="text-gray-500 text-sm whitespace-nowrap">
              {field.label}:
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="font-bold text-sm text-right truncate flex-1 leading-snug cursor-default">
                  {formatValue(field.value, field.format, field.currencyCode)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs break-words">
                <p>{formatValue(field.value, field.format, field.currencyCode)}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        ))}
      </div>

      {/* Footer - always at bottom */}
      <div className="flex items-center justify-between mt-4">
        {!hideSelection ? (
          <div className="flex items-center gap-2">
            {/* Select/Selected/Match button */}
            <Button
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => {
                if (oppositeSelected && !isSelected && onMatch) {
                  // When Match is clicked, create the match instead of selecting
                  onMatch(allowPartialMatch ? percentage : undefined);
                } else {
                  // Normal select/deselect behavior
                  handleSelect(!isSelected);
                }
              }}
              disabled={otherSelected}
              className={cn(
                isSelected && "bg-blue-600 hover:bg-blue-700",
                oppositeSelected && !isSelected && "bg-blue-600 hover:bg-blue-700 text-white"
              )}
            >
              {isSelected ? "Selected" : oppositeSelected ? "Match" : "Select"}
            </Button>

            {/* Percentage selector combobox for invoices */}
            {allowPartialMatch && (
              <div className="relative">
                <Select
                  value={selectValue}
                  onValueChange={handlePresetPercentageChange}
                  onOpenChange={(open) => {
                    if (open && selectValue === 'custom') {
                      requestAnimationFrame(() => {
                        customPercentageInputRef.current?.focus();
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-24 h-9">
                    <SelectValue placeholder={`${percentage}%`}>
                      {`${percentage}%`}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom" className="hidden" aria-hidden="true">
                      {`${percentage}%`}
                    </SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="20">20%</SelectItem>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="100">100%</SelectItem>
                    <div className="px-2 py-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        ref={customPercentageInputRef}
                        value={customInputValue}
                        onChange={(e) => handleCustomPercentageChange(e.target.value)}
                        onFocus={() => setIsEditingCustom(true)}
                        onBlur={ensureCustomValueOnBlur}
                        placeholder="Custom %"
                        className="h-8 text-sm"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          setIsEditingCustom(true);
                          requestAnimationFrame(() => {
                            customPercentageInputRef.current?.focus();
                          });
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        onKeyDownCapture={(e) => e.stopPropagation()}
                        onKeyUpCapture={(e) => e.stopPropagation()}
                      />
                    </div>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ) : (
          <div></div> // Empty div to maintain flex layout
        )}

        <div className="flex items-center gap-2">
          {onViewItem && (
            <button
              onClick={onViewItem}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              {getViewButtonText()}
            </button>
          )}

          {showDeleteButton && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeleteClick}
              disabled={isDeleting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {type === 'invoice' ? 'Invoice' : 'Transaction'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this {type === 'invoice' ? 'invoice' : 'transaction'}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                `Delete ${type === 'invoice' ? 'Invoice' : 'Transaction'}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ItemCard;
